import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { Toaster, toast } from 'sonner';
import { Check, ChevronsUpDown, Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { getSavedPDFs, generateAndSavePagePDF, debugSavedPDFs } from '@/utils/pdfGenerator';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import html2pdf from 'html2pdf.js';
import { PDFDocument } from 'pdf-lib';
import PageIndicator from '@/components/PageIndicator';
import { PAGE_TITLES } from '@/components/PDFTemplates';
import { SOUTH_AFRICAN_BANKS, validateAccountNumber } from '@/utils/bankData';
import './PageSeven.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URLs for header
// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

const PageSeven = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state || {};

  // Build full name from firstName, secondName, middleName, and surname (from Page 1)
  const fullName = [
    clientData.firstName,
    clientData.secondName,
    clientData.middleName,
    clientData.surname
  ].filter(Boolean).join(' ');

  // Pre-fill form data from clientData (Page 1 values)
  const [formData, setFormData] = useState({
    account_holder_name: fullName || clientData.clientName || clientData.name || '',
    id_number: clientData.idNumber || '',
    physical_address: clientData.physicalAddress || clientData.address || '',
    bank_name: clientData.page7BankName || '',
    branch_name: clientData.page7BranchName || '',
    branch_code: clientData.page7BranchCode || '',
    account_number: clientData.page7AccountNumber || '',
    account_type: clientData.page7AccountType || 'Current',
    signing_date: clientData.page7SigningDate || new Date().toISOString().split('T')[0],
    contact_number: clientData.cellphoneNumber || clientData.contactNumber || clientData.phone || '',
    email: clientData.emailAddress || clientData.email || '',
    amount: 'Invoice amount - variable amounts allowed',
    contract_reference: clientData.page7ContractReference || '',
  });

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [emailSendFailed, setEmailSendFailed] = useState(false);
  const [savedFormId, setSavedFormId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openBankCombobox, setOpenBankCombobox] = useState(false);
  const [contactNumberError, setContactNumberError] = useState('');
  const [contractReferenceError, setContractReferenceError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [idNumberError, setIdNumberError] = useState('');
  const [accountNumberWarning, setAccountNumberWarning] = useState('');
  const [showSavingModal, setShowSavingModal] = useState(false);
  const [savingCountdown, setSavingCountdown] = useState(120); // 2 minutes in seconds
  const [timerResetCount, setTimerResetCount] = useState(0); // Track how many times timer has reset
  const [showAddressTooltip, setShowAddressTooltip] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({ fields: [], errors: [], acknowledgements: [] });
  const [acknowledgements, setAcknowledgements] = useState({
    agreement1: clientData.page7Acknowledgements?.agreement1 || false,
    agreement2: clientData.page7Acknowledgements?.agreement2 || false,
    agreement3: clientData.page7Acknowledgements?.agreement3 || false,
    agreement4: clientData.page7Acknowledgements?.agreement4 || false,
    agreement5: clientData.page7Acknowledgements?.agreement5 || false,
  });
  const [savedSignature, setSavedSignature] = useState(clientData.page7Signature || null);

  const signatureRef = useRef(null);
  const signatureDataRef = useRef(savedSignature || null); // Store signature data for restoration
  
  // Get formId from clientData
  const formId = clientData.formId;
  
  // Function to restore signature to canvas
  const restoreSignature = () => {
    if (signatureDataRef.current && signatureRef.current) {
      signatureRef.current.fromDataURL(signatureDataRef.current);
    }
  };
  
  // Restore signature when component mounts if we have a saved signature
  useEffect(() => {
    if (savedSignature && signatureRef.current) {
      // Load the saved signature back into the canvas
      signatureRef.current.fromDataURL(savedSignature);
      signatureDataRef.current = savedSignature;
    }
  }, [savedSignature]);
  
  // Handle resize events - restore signature after resize
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        restoreSignature();
      }, 500);
    };

    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, []);
  
  // Countdown timer effect for saving modal
  useEffect(() => {
    let timer;
    if (showSavingModal && savingCountdown > 0) {
      timer = setInterval(() => {
        setSavingCountdown(prev => prev - 1);
      }, 1000);
    } else if (showSavingModal && savingCountdown === 0) {
      // Timer reached 0, reset to 2 minutes and increment reset count
      setSavingCountdown(120);
      setTimerResetCount(prev => prev + 1);
    }
    return () => clearInterval(timer);
  }, [showSavingModal, savingCountdown]);
  
  // Reset countdown and reset count when modal opens
  useEffect(() => {
    if (showSavingModal) {
      setSavingCountdown(120);
      setTimerResetCount(0);
    }
  }, [showSavingModal]);

  // Scroll to top on component mount
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 0);
  }, []);

  // Auto-generate contract reference when name is set
  useEffect(() => {
    if (formData.account_holder_name && formData.account_holder_name.trim().length >= 3 && !formData.contract_reference) {
      const firstThree = formData.account_holder_name.trim().substring(0, 3).toUpperCase();
      setFormData(prev => ({
        ...prev,
        contract_reference: `${firstThree}001`
      }));
    }
  }, [formData.account_holder_name, formData.contract_reference]);

  const validateEmail = (value) => {
    if (!value) return '';
    const email = value.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    
    const commonDomains = {
      'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com', 'gmail.co': 'gmail.com', 'gnail.com': 'gmail.com',
      'hotmial.com': 'hotmail.com', 'hotmail.co': 'hotmail.com',
      'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahoo.co': 'yahoo.com',
      'outlok.com': 'outlook.com', 'outllook.com': 'outlook.com', 'outlook.co': 'outlook.com',
    };
    const domain = email.split('@')[1];
    if (commonDomains[domain]) return `Did you mean ${email.split('@')[0]}@${commonDomains[domain]}?`;
    return '';
  };

  const validateIdNumber = (idNumber) => {
    if (!idNumber) return '';
    const cleanId = idNumber.replace(/\D/g, '');
    
    if (cleanId.length !== 13) return 'South African ID number must be exactly 13 digits';
    
    const year = cleanId.substring(0, 2);
    const month = cleanId.substring(2, 4);
    const day = cleanId.substring(4, 6);
    const currentYear = new Date().getFullYear() % 100;
    const fullYear = parseInt(year) <= currentYear + 10 ? 2000 + parseInt(year) : 1900 + parseInt(year);
    
    const monthNum = parseInt(month);
    if (monthNum < 1 || monthNum > 12) return 'Invalid birth month in ID number';
    
    const dayNum = parseInt(day);
    const daysInMonth = new Date(fullYear, monthNum, 0).getDate();
    if (dayNum < 1 || dayNum > daysInMonth) return 'Invalid birth day in ID number';
    
    const birthDate = new Date(fullYear, monthNum - 1, dayNum);
    if (birthDate > new Date()) return 'Birth date cannot be in the future';
    
    const genderSeq = parseInt(cleanId.substring(6, 10));
    if (isNaN(genderSeq) || genderSeq < 0 || genderSeq > 9999) return 'Invalid gender sequence in ID number';
    
    const citizenship = cleanId.charAt(10);
    if (!['0', '1', '2'].includes(citizenship)) return 'Invalid citizenship indicator in ID number';
    
    // Luhn checksum validation
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      let digit = parseInt(cleanId.charAt(i));
      if (i % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
    }
    const calculatedChecksum = (10 - (sum % 10)) % 10;
    const checksumDigit = parseInt(cleanId.charAt(12));
    if (calculatedChecksum !== checksumDigit) return 'ID number checksum validation failed. Please verify that the number is correct.';
    
    return '';
  };

  const validateContactNumber = (value) => {
    const cleanedValue = value.replace(/\s+/g, '');
    if (!cleanedValue) return '';
    if (!cleanedValue.startsWith('0')) return 'Contact number must start with 0';
    if (cleanedValue.length < 10) return 'Contact number must be at least 10 digits';
    return '';
  };

  const validateContractReference = (value) => {
    if (!value) return '';
    if (value.length !== 6) return 'Contract reference must be exactly 6 characters';
    const firstThree = value.substring(0, 3);
    const lastThree = value.substring(3, 6);
    if (!/^[A-Za-z]{3}$/.test(firstThree)) return 'First 3 characters must be letters';
    if (!/^\d{3}$/.test(lastThree)) return 'Last 3 characters must be numbers';
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'email') setEmailError(validateEmail(value));
    if (name === 'contact_number') setContactNumberError(validateContactNumber(value));
    if (name === 'id_number') setIdNumberError(validateIdNumber(value));
    if (name === 'account_number') setAccountNumberWarning(validateAccountNumber(value, formData.bank_name));
    
    if (name === 'contract_reference') {
      if (value.length > 6) return;
      setContractReferenceError(validateContractReference(value));
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase() }));
      return;
    }
    
    if (name === 'account_holder_name') {
      let contractRef = '';
      if (value.trim().length >= 3) {
        const firstThree = value.trim().substring(0, 3).toUpperCase();
        contractRef = `${firstThree}001`;
        setContractReferenceError(validateContractReference(contractRef));
      }
      setFormData(prev => ({ ...prev, [name]: value, contract_reference: contractRef }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
    signatureDataRef.current = null; // Clear stored signature data too
  };

  const handleAcknowledgementChange = (agreementKey) => {
    setAcknowledgements(prev => ({ ...prev, [agreementKey]: !prev[agreementKey] }));
  };

  const validateForm = () => {
    const missingFields = [];
    const errors = [];
    
    // Note: Auto-populated fields from Page 1 are excluded from validation
    // (account_holder_name, id_number, physical_address, contact_number, email)
    // These are read-only and come from Page 1
    
    // Only validate fields that the user must fill on this page
    if (!formData.bank_name.trim()) missingFields.push('Bank Name');
    if (!formData.account_number.trim()) missingFields.push('Account Number');
    if (!formData.contract_reference.trim()) missingFields.push('Contract Reference Number');
    
    // Re-validate account number to catch any validation errors
    // This ensures validation is checked even if user didn't trigger onBlur
    if (formData.bank_name && formData.account_number) {
      const accountValidationError = validateAccountNumber(formData.account_number, formData.bank_name);
      if (accountValidationError) {
        errors.push(`Account Number: ${accountValidationError}`);
      }
    }
    
    // Check contract reference validation error
    if (contractReferenceError) errors.push(`Contract Reference: ${contractReferenceError}`);
    
    // Check signature
    if (signatureRef.current?.isEmpty()) missingFields.push('Account Holder Signature');
    
    // Check acknowledgements
    const uncheckedAgreements = [];
    if (!acknowledgements.agreement1) uncheckedAgreements.push('Netcash authorization');
    if (!acknowledgements.agreement2) uncheckedAgreements.push('Cancellation liability');
    if (!acknowledgements.agreement3) uncheckedAgreements.push('Administrative surcharge');
    if (!acknowledgements.agreement4) uncheckedAgreements.push('Variable mandate');
    if (!acknowledgements.agreement5) uncheckedAgreements.push('Dishonoured debit order fee');
    
    if (missingFields.length > 0 || errors.length > 0 || uncheckedAgreements.length > 0) {
      setValidationErrors({
        fields: missingFields,
        errors: errors,
        acknowledgements: uncheckedAgreements
      });
      setShowValidationModal(true);
      return false;
    }
    return true;
  };

  // Generate PDF from HTML element
  const generatePDFFromElement = async (element, pageNum) => {
    const signedDateTime = new Date();
    const formattedDate = signedDateTime.toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });
    const formattedTime = signedDateTime.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const signedText = `Generated: ${formattedDate} at ${formattedTime}`;

    const options = {
      margin: [10, 10, 15, 10],
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { 
        scale: 2, 
        useCORS: true, 
        allowTaint: true,
        logging: false,  // Reduce console noise
        backgroundColor: '#ffffff',
        imageTimeout: 15000,
        scrollX: 0,
        scrollY: 0
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: ['img', 'tr', 'h1', 'h2', 'h3', 'h4', 'p', 'div', 'section', 'li', '.avoid-break']
      }
    };

    try {
      const pdf = await html2pdf().set(options).from(element).toPdf().get('pdf');
      
      const totalSheets = pdf.internal.getNumberOfPages();
      for (let sheet = 1; sheet <= totalSheets; sheet++) {
        pdf.setPage(sheet);
        pdf.setFontSize(9);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Document ${String(pageNum).padStart(2, '0')} of 07 - Sheet ${sheet}/${totalSheets}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, (210 - pageTextWidth) / 2, 287);
        pdf.setTextColor(80, 80, 80);
        const signedTextWidth = pdf.getTextWidth(signedText);
        pdf.text(signedText, 210 - 10 - signedTextWidth, 283);
      }

      const output = pdf.output('dataurlstring');
      console.log(`PDF element generation for page ${pageNum}: ${output?.length || 0} chars`);
      return output;
    } catch (error) {
      console.error(`Error in generatePDFFromElement for page ${pageNum}:`, error);
      throw error;
    }
  };

  // Merge multiple PDFs into one
  const mergePDFs = async (pdfDataArray) => {
    const mergedPdf = await PDFDocument.create();
    
    for (const pdfItem of pdfDataArray) {
      try {
        const base64Data = pdfItem.data.split(',')[1];
        const pdfBytes = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
        const pdf = await PDFDocument.load(pdfBytes);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      } catch (error) {
        console.error(`Error merging PDF for page ${pdfItem.pageNum}:`, error);
      }
    }
    
    const mergedPdfBytes = await mergedPdf.save();
    
    // Convert Uint8Array to base64 in chunks to avoid call stack issues
    const chunkSize = 8192;
    let base64 = '';
    for (let i = 0; i < mergedPdfBytes.length; i += chunkSize) {
      const chunk = mergedPdfBytes.slice(i, i + chunkSize);
      base64 += String.fromCharCode.apply(null, chunk);
    }
    base64 = btoa(base64);
    
    return `data:application/pdf;base64,${base64}`;
  };

  const handleFinalize = async () => {
    if (!validateForm()) return;

    try {
      setIsLoading(true);
      setShowSavingModal(true);
      
      // Restore signature to canvas before PDF generation (in case it was cleared by zoom/resize)
      restoreSignature();
      
      const signatureData = signatureRef.current.toDataURL();
      const clientName = formData.account_holder_name.replace(/[^a-zA-Z0-9]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      
      // Get formId from clientData
      const formId = clientData.formId;
      
      // Array to store individual PDFs
      const individualPDFs = [];
      
      toast.info('Saving the PDF of the Debit Order Mandate...');
      
      console.log('=== PDF GENERATION START (NEW APPROACH) ===');
      console.log('formId:', formId);
      console.log('clientData keys:', Object.keys(clientData).join(', '));

      // Step 1: Generate PDF for the Debit Order Mandate (current page) with all custom
      // modifications, and save it to backend under the given page number. Reused below to
      // silently generate an alternate "Page 8" mandate copy when bank details are mismatched.
      const generateDebitFormPDF = async (pageNumberToSave) => {
      try {
        console.log(`[Page7] Generating PDF (page ${pageNumberToSave}) from current page with custom modifications...`);
        
        const element = document.querySelector('.debit-form-wrapper');
        if (!element) {
          throw new Error('Could not find .debit-form-wrapper element');
        }
        
        // Store original values for restoration
        const originalStyles = new Map();
        
        const hideElement = (el) => {
          if (el) {
            originalStyles.set(el, el.style.cssText);
            el.style.display = 'none';
          }
        };
        
        const modifyStyle = (el, newStyle) => {
          if (el) {
            if (!originalStyles.has(el)) {
              originalStyles.set(el, el.style.cssText);
            }
            Object.assign(el.style, newStyle);
          }
        };
        
        // === PDF MODIFICATIONS FOR PAGE 7 ===
        
        // 1. HIDE the logo section (TCA logo and banner) - target the parent main element
        const mainElement = document.querySelector('main.flex-1');
        if (mainElement) {
          const logoSection = mainElement.querySelector('.mb-4.sm\\:mb-6.flex.flex-col.items-center');
          if (logoSection) hideElement(logoSection);
          
          const mainHeading = mainElement.querySelector('.mb-6.pb-2');
          if (mainHeading) hideElement(mainHeading);
          
          const explanatoryNote = mainElement.querySelector('.bg-blue-50.border.border-blue-200');
          if (explanatoryNote) hideElement(explanatoryNote);
        }
        
        // 2. Keep header at original sizes - only adjust spacing
        element.querySelectorAll('.debit-logo').forEach(el => {
          modifyStyle(el, { height: '120px', objectFit: 'contain' });
        });
        
        element.querySelectorAll('.debit-form-header').forEach(el => {
          modifyStyle(el, { padding: '20px 40px 8px 40px' });
        });
        
        element.querySelectorAll('.debit-banner').forEach(el => {
          modifyStyle(el, { marginBottom: '2px', marginTop: '0' });
        });
        element.querySelectorAll('.debit-form-title').forEach(el => {
          modifyStyle(el, { marginTop: '0', marginBottom: '2px' });
        });
        
        element.querySelectorAll('.debit-form-subtitle').forEach(el => {
          modifyStyle(el, { paddingBottom: '6px', marginBottom: '0', paddingTop: '0' });
        });
        
        element.querySelectorAll('.debit-form-content').forEach(el => {
          modifyStyle(el, { padding: '0px 40px 20px 40px', marginTop: '0' });
        });
        
        element.querySelectorAll('.debit-section-title').forEach(el => {
          modifyStyle(el, { marginTop: '0', paddingTop: '0', marginBottom: '8px' });
        });
        
        element.querySelectorAll('.debit-form-section').forEach((el) => {
          modifyStyle(el, { 
            marginBottom: '16px', 
            marginTop: '0',
            paddingTop: '0',
            pageBreakInside: 'auto',
            breakInside: 'auto'
          });
        });
        
        element.querySelectorAll('.debit-form-group').forEach(el => {
          modifyStyle(el, { 
            pageBreakInside: 'auto',
            breakInside: 'auto',
            marginBottom: '4px'
          });
        });
        
        element.querySelectorAll('.debit-form-row').forEach(el => {
          modifyStyle(el, { marginBottom: '2px' });
        });
        
        // Insert page break before Email Address row
        element.querySelectorAll('input[name="email"]').forEach(emailInput => {
          const formRow = emailInput.closest('.debit-form-row');
          const formGroup = emailInput.closest('.debit-form-group');
          if (formRow) {
            modifyStyle(formRow, { 
              pageBreakBefore: 'always',
              breakBefore: 'always',
              paddingTop: '8px'
            });
          } else if (formGroup) {
            modifyStyle(formGroup, { 
              pageBreakBefore: 'always',
              breakBefore: 'always',
              paddingTop: '8px'
            });
          }
        });
        
        element.querySelectorAll('.debit-form-section').forEach((el) => {
          modifyStyle(el, { 
            marginBottom: '10px', 
            marginTop: '0',
            paddingTop: '0',
            pageBreakInside: 'auto',
            breakInside: 'auto'
          });
        });
        
        element.querySelectorAll('.debit-section-title').forEach(el => {
          modifyStyle(el, { marginBottom: '4px', paddingBottom: '2px', marginTop: '0' });
        });
        
        // Insert page break before C. CANCELLATION
        element.querySelectorAll('.debit-section-title').forEach(el => {
          if (el.textContent.includes('C.') && el.textContent.includes('CANCELLATION')) {
            const parentSection = el.closest('.debit-form-section');
            if (parentSection) {
              modifyStyle(parentSection, { 
                pageBreakBefore: 'always',
                breakBefore: 'always',
                paddingTop: '12px'
              });
            } else {
              modifyStyle(el, { 
                pageBreakBefore: 'always',
                breakBefore: 'always',
                paddingTop: '12px'
              });
            }
          }
        });
        
        element.querySelectorAll('.clause-section, .mandate-clause').forEach(el => {
          modifyStyle(el, { 
            marginBottom: '2px', 
            marginTop: '0px',
            paddingBottom: '0px'
          });
        });
        
        element.querySelectorAll('.debit-form-section').forEach(el => {
          modifyStyle(el, { 
            marginBottom: '6px', 
            marginTop: '0',
            paddingTop: '0',
            paddingBottom: '0',
            pageBreakInside: 'auto',
            breakInside: 'auto'
          });
        });
        
        element.querySelectorAll('.acknowledgement-item, .checkbox-item, .agreement-checkbox').forEach(el => {
          modifyStyle(el, { 
            marginBottom: '1px',
            paddingBottom: '0px'
          });
        });
        
        element.querySelectorAll('.checkbox-label, .acknowledgement-label, label').forEach(el => {
          const checkbox = el.querySelector('input[type="checkbox"]') || el.previousElementSibling;
          if (checkbox && checkbox.type === 'checkbox') {
            modifyStyle(el, { 
              verticalAlign: 'top',
              marginTop: '-2px',
              lineHeight: '1.15',
              display: 'inline-block'
            });
          }
        });
        
        element.querySelectorAll('.checkbox-wrapper, .agreement-item, .flex.items-start').forEach(el => {
          modifyStyle(el, { 
            alignItems: 'flex-start',
            verticalAlign: 'top',
            marginBottom: '1px'
          });
        });
        
        element.querySelectorAll('.debit-form-section p').forEach(el => {
          modifyStyle(el, { 
            marginBottom: '1px',
            lineHeight: '1.25'
          });
        });
        
        element.querySelectorAll('.signature-section').forEach(el => {
          modifyStyle(el, { 
            marginTop: '0px',
            paddingTop: '0px',
            paddingBottom: '0px'
          });
        });
        
        element.querySelectorAll('.signature-container').forEach(el => {
          modifyStyle(el, { 
            overflow: 'hidden',
            marginBottom: '0px',
            marginTop: '0px',
            transform: 'scale(0.98)',
            transformOrigin: 'top left'
          });
        });
        
        // Hide "Please draw your signature in the box below:"
        element.querySelectorAll('p, span, label').forEach(el => {
          if (el.textContent.includes('Please draw your signature in the box below')) {
            hideElement(el);
          }
        });
        
        element.querySelectorAll('.signature-label, .signature-text, .signature-section p, .signature-section span').forEach(el => {
          modifyStyle(el, { 
            marginTop: '0px',
            paddingTop: '0px',
            marginBottom: '0px',
            paddingBottom: '0px'
          });
        });
        
        element.querySelectorAll('.signature-section').forEach(el => {
          const nextSibling = el.nextElementSibling;
          if (nextSibling) {
            modifyStyle(nextSibling, {
              marginTop: '0px',
              paddingTop: '0px'
            });
          }
        });
        
        element.querySelectorAll('input, textarea').forEach(el => {
          if (el.name !== 'physical_address') {
            modifyStyle(el, { 
              verticalAlign: 'top',
              paddingTop: '8px',
              paddingBottom: '8px'
            });
          }
        });
        
        // FIX date format - convert signing_date from YYYY-MM-DD to DD/MM/YYYY
        element.querySelectorAll('input[name="signing_date"]').forEach(dateInput => {
          const dateValue = dateInput.value || '';
          if (dateValue && dateValue.includes('-')) {
            const [year, month, day] = dateValue.split('-');
            const formattedDate = `${day}/${month}/${year}`;
            
            const dateDisplay = document.createElement('input');
            dateDisplay.type = 'text';
            dateDisplay.value = formattedDate;
            dateDisplay.readOnly = true;
            dateDisplay.className = 'date-display-pdf';
            dateDisplay.style.cssText = `
              width: 100%;
              padding: 8px 16px;
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              font-size: 1rem;
              font-family: 'IBM Plex Sans', sans-serif;
              background: #fafafa;
              height: 44px;
              line-height: 1.5;
              box-sizing: border-box;
              vertical-align: top;
              display: block;
            `;
            
            hideElement(dateInput);
            dateInput.parentNode.insertBefore(dateDisplay, dateInput.nextSibling);
          }
        });
        
        // FIX physical address to show line breaks properly
        element.querySelectorAll('textarea[name="physical_address"]').forEach(textarea => {
          const addressValue = textarea.value || '';
          const addressDiv = document.createElement('div');
          addressDiv.className = 'address-display-pdf';
          addressDiv.style.cssText = `
            width: 100%;
            padding: 12px 16px;
            border: 2px solid #93c5fd;
            border-radius: 8px;
            font-size: 1rem;
            font-family: 'IBM Plex Sans', sans-serif;
            background: #f0f9ff;
            color: #1e40af;
            white-space: pre-line;
            line-height: 1.5;
            min-height: 100px;
          `;
          addressDiv.innerText = addressValue;
          
          hideElement(textarea);
          textarea.parentNode.insertBefore(addressDiv, textarea.nextSibling);
        });
        
        // FIX Bank Name - show hidden input field, hide button
        element.querySelectorAll('.bank-name-pdf-field').forEach(el => {
          modifyStyle(el, { 
            display: 'block',
            width: '100%',
            padding: '12px 16px',
            border: '2px solid #e5e7eb',
            borderRadius: '8px',
            fontSize: '1rem',
            background: '#fafafa'
          });
        });
        element.querySelectorAll('.bank-selector-button').forEach(el => {
          hideElement(el);
        });
        element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
        
        element.querySelectorAll('p').forEach(el => {
          modifyStyle(el, { marginBottom: '6px', lineHeight: '1.4' });
        });
        
        element.querySelectorAll('h1, h2, h3, h4').forEach(el => {
          modifyStyle(el, { marginTop: '6px', marginBottom: '6px' });
        });
        
        element.querySelectorAll('.no-print').forEach(el => hideElement(el));
        
        element.querySelectorAll('ul').forEach(ul => {
          modifyStyle(ul, { marginTop: '4px', marginBottom: '4px' });
        });
        element.querySelectorAll('li').forEach(li => {
          modifyStyle(li, { marginBottom: '3px', lineHeight: '1.4' });
        });
        
        element.querySelectorAll('.info-section, .info-section-header').forEach(el => {
          modifyStyle(el, { 
            marginTop: '8px', 
            marginBottom: '8px',
            padding: '12px 16px'
          });
        });
        
        element.querySelectorAll('.authorization-text').forEach(el => {
          modifyStyle(el, { 
            marginTop: '6px', 
            marginBottom: '6px',
            padding: '10px 14px'
          });
        });
        element.querySelectorAll('.debit-form-row').forEach(el => {
          modifyStyle(el, { marginBottom: '12px' });
        });
        
        // Generate PDF (no footer to achieve 3-page goal)
        const pdfOptions = {
          margin: [3, 4, 3, 4],
          filename: `Page${String(pageNumberToSave).padStart(2, '0')}_${formId || 'form'}.pdf`,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: { 
            scale: 2, 
            useCORS: true, 
            logging: false, 
            letterRendering: true,
            allowTaint: false,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            windowWidth: element.scrollWidth,
            // Force grid columns in cloned document for consistent PDF layout
            onclone: (clonedDoc) => {
              const clonedElement = clonedDoc.querySelector('#page-pdf-content');
              if (clonedElement) {
                // Force grid columns to ensure multi-column layout in PDF
                clonedElement.querySelectorAll('.grid').forEach(grid => {
                  const classList = grid.className;
                  if (classList.includes('grid-cols-2') || classList.includes('sm:grid-cols-2') || 
                      classList.includes('md:grid-cols-2') || classList.includes('lg:grid-cols-2')) {
                    grid.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
                  }
                  if (classList.includes('grid-cols-3') || classList.includes('sm:grid-cols-3') || 
                      classList.includes('md:grid-cols-3') || classList.includes('lg:grid-cols-3')) {
                    grid.style.gridTemplateColumns = 'repeat(3, minmax(0, 1fr))';
                  }
                  if (classList.includes('grid-cols-4') || classList.includes('sm:grid-cols-4') || 
                      classList.includes('md:grid-cols-4') || classList.includes('lg:grid-cols-4')) {
                    grid.style.gridTemplateColumns = 'repeat(4, minmax(0, 1fr))';
                  }
                });
              }
            }
          },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
          pagebreak: { mode: ['css', 'legacy'] }
        };
        
        const pdfBlob = await html2pdf().set(pdfOptions).from(element).outputPdf('blob');
        
        // Restore all original styles
        originalStyles.forEach((originalStyle, el) => {
          el.style.cssText = originalStyle;
        });
        
        // Remove temporary elements
        element.querySelectorAll('.address-display-pdf').forEach(el => el.remove());
        element.querySelectorAll('.date-display-pdf').forEach(el => el.remove());
        
        // Save PDF to backend using the centralized PDF storage endpoint - wait for completion before proceeding
        await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = async () => {
            const base64data = reader.result.split(',')[1];
            try {
              const response = await fetch(`${BACKEND_URL}/api/pdf/save-page`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  form_id: formId,
                  page_number: pageNumberToSave,
                  pdf_data: base64data,
                  page_title: 'Debit Order Mandate',
                  client_data: clientData
                })
              });
              if (response.ok) {
                const result = await response.json();
                console.log(`[Page7] PDF saved to backend successfully, size: ${result.pdf_size} bytes`);
                resolve();
              } else {
                console.error('[Page7] Failed to save PDF to backend:', response.status);
                reject(new Error('Failed to save PDF'));
              }
            } catch (err) {
              console.error('[Page7] Error saving PDF to backend:', err);
              reject(err);
            }
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(pdfBlob);
        });
        
        console.log('[Page7] PDF generated with custom modifications');
        
      } catch (e) { 
        console.error('[Page7] PDF generation error:', e); 
      }
      };

      await generateDebitFormPDF(7);

      // Fraud-safeguard: if the debit order bank details differ from the Page 1 baseline bank
      // details ("Bank Account Details for Tax Refund"), silently generate a duplicate mandate
      // (Page 8) using the Page 1 bank details, then restore the client's originally submitted
      // values. Page 8 is only ever attached to the internal admin-only email - it must never
      // reach the client's copy of the agreement.
      try {
        const page1BankRaw = (clientData.page1BankName || '').trim();
        const page1AcctRaw = (clientData.page1AccountNumber || '').replace(/\s/g, '');
        const enteredBank = (formData.bank_name || '').trim();
        const enteredAcct = (formData.account_number || '').replace(/\s/g, '');

        if (page1BankRaw && page1AcctRaw && (page1BankRaw.toLowerCase() !== enteredBank.toLowerCase() || page1AcctRaw !== enteredAcct)) {
          const matchedBank = SOUTH_AFRICAN_BANKS.find((b) => b.label.toLowerCase() === page1BankRaw.toLowerCase());
          const originalBankSnapshot = {
            bank_name: formData.bank_name,
            branch_name: formData.branch_name,
            branch_code: formData.branch_code,
            account_number: formData.account_number,
            account_type: formData.account_type,
          };
          const originalAccountWarning = accountNumberWarning;

          await new Promise((resolve) => {
            setAccountNumberWarning('');
            setFormData((prev) => ({
              ...prev,
              bank_name: clientData.page1BankName || '',
              branch_name: matchedBank ? 'UNIVERSAL BRANCH' : (clientData.page1BranchName || ''),
              branch_code: matchedBank ? matchedBank.branchCode : (clientData.page1BranchCode || ''),
              account_number: clientData.page1AccountNumber || '',
              account_type: clientData.page1AccountType || 'Current',
            }));
            setTimeout(resolve, 250);
          });

          await generateDebitFormPDF(8);

          await new Promise((resolve) => {
            setFormData((prev) => ({ ...prev, ...originalBankSnapshot }));
            setAccountNumberWarning(originalAccountWarning);
            setTimeout(resolve, 250);
          });
        }
      } catch (dupErr) {
        console.error('[Page7] Alternate mandate (Page 8) generation error:', dupErr);
      }

      // Step 2: Fetch all PDFs (pages 1-7, plus Page 8 if generated) from the backend
      toast.info('Combining all pages into Final Signed Agreement...');
      
      try {
        // Debug: Check what PDFs are stored
        const debugInfo = await debugSavedPDFs(formId);
        console.log('Debug - Stored PDFs:', debugInfo);
        
        // Fetch all saved PDFs
        const savedPDFs = await getSavedPDFs(formId);
        console.log(`Retrieved ${savedPDFs.count} PDFs from backend`);
        
        if (savedPDFs.count === 0) {
          console.error('No PDFs found in backend! Did you navigate through all pages?');
          toast.error('No PDFs found. Please navigate through all pages first.');
          setIsLoading(false);
          setShowSavingModal(false);
          return;
        }
        
        // Convert to the format expected by the rest of the code
        for (const page of savedPDFs.pages) {
          console.log(`Page ${page.page_number}: ${page.page_title}, size: ${page.pdf_size} bytes`);
          individualPDFs.push({
            pageNum: page.page_number,
            data: page.pdf_data.startsWith('data:') ? page.pdf_data : `data:application/pdf;base64,${page.pdf_data}`,
            title: page.page_title
          });
        }
        
        console.log(`Total PDFs to merge: ${individualPDFs.length}`);
        
      } catch (fetchError) {
        console.error('Error fetching saved PDFs:', fetchError);
        toast.error('Failed to fetch saved PDFs from server.');
        setIsLoading(false);
        setShowSavingModal(false);
        return;
      }

      // Step 3: Generate combined PDF (client-facing pages only - Page 8, if any, is excluded
      // here and only ever attached separately to the internal admin-only email)
      let combinedPdf = null;
      try {
        const clientFacingPDFs = individualPDFs.filter((pdf) => pdf.pageNum <= 7);
        console.log(`Merging ${clientFacingPDFs.length} PDFs...`);
        clientFacingPDFs.forEach((pdf, i) => {
          console.log(`PDF ${i + 1}: pageNum=${pdf.pageNum}, dataLength=${pdf.data?.length || 0}`);
        });
        
        if (clientFacingPDFs.length > 0) {
          combinedPdf = await mergePDFs(clientFacingPDFs);
          console.log(`Combined PDF created, length: ${combinedPdf?.length || 0}`);
        } else {
          console.error('No individual PDFs were generated');
          toast.error('Failed to generate individual PDFs');
        }
      } catch (e) { 
        console.error('Combined PDF error:', e);
        toast.error('Failed to create combined PDF');
      }

      // Save form data to database
      const response = await fetch(`${API}/debit-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, signature_data: signatureData }),
      });
      
      if (!response.ok) throw new Error('Failed to save form');
      const result = await response.json();
      setSavedFormId(result.id);
      
      // Send emails with PDFs
      try {
        const emailResponse = await fetch(`${API}/debit-order/send-emails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form_id: result.id,
            combined_pdf: combinedPdf,
            individual_pdfs: individualPDFs.map(p => ({
              pageNum: p.pageNum,
              title: p.title,
              data: p.data
            })),
            form_data: formData,
            client_data: clientData,
            client_name: clientName,
            pop_filename: clientData.popFilename || null,  // Include Proof of Payment filename if Manual EFT was used
          }),
        });
        const emailResult = await emailResponse.json();

        if (emailResult?.combined_sent === false) {
          // The client-facing combined email failed to send even after retries
          setEmailSendFailed(true);
          toast.error('We could not confirm delivery of your confirmation email. Please see the notice below.', { duration: 8000 });
        } else {
          setEmailSendFailed(false);
          // Toast 1: Appears immediately
          toast.success('Tax Services Agreement successfully completed!', { duration: 5000 });

          // Toast 2: Appears 2 seconds later
          setTimeout(() => {
            toast.success('Emails sent successfully!', { duration: 5000 });
          }, 2000);

          // Toast 3: Appears 4 seconds later (2 seconds after toast 2)
          setTimeout(() => {
            toast.success('All done! You may proceed to click \'Thank You\' or close this browser tab!', { duration: 5000 });
          }, 4000);
        }
      } catch (emailError) {
        console.error('Email sending error:', emailError);
        setEmailSendFailed(true);
        toast.warning('PDFs generated but email sending failed.', { duration: 5000 });
      }
      
      setShowSavingModal(false);
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Error in finalization:', error);
      setShowSavingModal(false);
      toast.error('Failed to complete. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleThankYou = () => {
    setShowSuccessDialog(false);
    // Navigate to completion page
    navigate('/complete');
  };

  const handleBack = () => {
    navigate('/page-6', { 
      state: {
        ...clientData,
        paymentCompleted: true,
        // Preserve Page 7 form data for when user returns
        page7BankName: formData.bank_name,
        page7BranchName: formData.branch_name,
        page7BranchCode: formData.branch_code,
        page7AccountNumber: formData.account_number,
        page7AccountType: formData.account_type,
        page7SigningDate: formData.signing_date,
        page7ContractReference: formData.contract_reference,
        // Preserve signature
        page7Signature: signatureRef.current ? signatureRef.current.toDataURL() : null,
        // Preserve acknowledgements
        page7Acknowledgements: acknowledgements,
      }
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Toaster position="top-right" richColors expand={true} visibleToasts={5} />
      
      <main className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img src={LOGO_URL} alt="TCA Auditors Logo" className="h-16 sm:h-20 w-auto object-contain" />
          <img src={BANNER_URL} alt="Tax & Auditing Consultants" className="h-10 sm:h-14 w-auto object-contain" />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* Explanatory Note */}
        <div className="w-full max-w-4xl bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="h-6 w-6 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-2">Why is this Debit Order Mandate included?</p>
              <p className="leading-relaxed">
                This Debit Order Mandate is included as part of your client file to ensure your records are complete 
                and comprehensive as part of our onboarding checklist. <strong>Please note:</strong> This mandate will
                <strong> not be actioned without prior communication</strong> and will only be used in the future should 
                you opt for the debit order facility to be activated.
              </p>
              <p className="mt-2 leading-relaxed">
                Think of this as being proactive — by completing this now, we create one comprehensive master agreement 
                between ourselves that includes all required documentation in a single PDF file, saving you time and 
                effort in the future.
              </p>
            </div>
          </div>
        </div>

        {/* Debit Order Form */}
        <div className="debit-form-wrapper w-full max-w-4xl">
          <div className="debit-form-header">
            <img src="/debit-order/image1.jpeg" alt="TC Auditors Logo" className="debit-logo" />
            <img src="/debit-order/company-banner.png" alt="TC Auditors Banner" className="debit-banner" />
            <h1 className="debit-form-title">DEBIT ORDER MANDATE FORM</h1>
            <p className="debit-form-subtitle">Electronic Authorization</p>
          </div>

          <div className="debit-form-content">
            <div className="debit-form-section">
              <h2 className="debit-section-title">A. AUTHORITY/MANDATE: ELECTRONIC</h2>

              <div className="debit-form-group">
                <label>Mandate given by (name of Accountholder) <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  name="account_holder_name"
                  value={formData.account_holder_name}
                  readOnly
                  className="auto-populated-field"
                />
              </div>

              <div className="debit-form-group">
                <label>ID Number <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  name="id_number"
                  value={formData.id_number}
                  readOnly
                  className="auto-populated-field"
                />
              </div>

              <div className="debit-form-group address-field-container">
                <label>Physical Address <span className="required-asterisk">*</span></label>
                <textarea
                  name="physical_address"
                  value={formData.physical_address}
                  readOnly
                  className="auto-populated-field"
                  rows="5"
                />
              </div>

              <div className="debit-form-row">
                <div className="debit-form-group">
                  <label>Bank Name (ABSA, FNB etc.) <span className="required-asterisk">*</span></label>
                  {/* Hidden input for PDF capture - shows the selected bank name */}
                  <input
                    type="text"
                    name="bank_name_display"
                    value={formData.bank_name || ''}
                    readOnly
                    className="bank-name-pdf-field"
                    style={{ display: 'none' }}
                    data-pdf-only="true"
                  />
                  <Popover open={openBankCombobox} onOpenChange={setOpenBankCombobox}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={openBankCombobox}
                        className="bank-selector-button"
                        data-hide-in-pdf="true"
                        style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                      >
                        <span style={{ flexGrow: 1, textAlign: 'left' }}>
                          {formData.bank_name
                            ? SOUTH_AFRICAN_BANKS.find((bank) => bank.label === formData.bank_name)?.label
                            : "Select bank..."}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="popover-content-bank">
                      <Command>
                        <CommandInput placeholder="Search bank..." />
                        <CommandList>
                          <CommandEmpty>No bank found.</CommandEmpty>
                          <CommandGroup>
                            {SOUTH_AFRICAN_BANKS.map((bank) => (
                              <CommandItem
                                key={bank.value}
                                value={bank.label}
                                onSelect={(currentValue) => {
                                  const selectedBank = SOUTH_AFRICAN_BANKS.find(
                                    (b) => b.label.toLowerCase() === currentValue.toLowerCase()
                                  );
                                  setFormData((prev) => ({
                                    ...prev,
                                    bank_name: currentValue === formData.bank_name ? '' : currentValue,
                                    branch_name: selectedBank ? 'UNIVERSAL BRANCH' : '',
                                    branch_code: selectedBank ? selectedBank.branchCode : '',
                                  }));
                                  if (formData.account_number) {
                                    setAccountNumberWarning(validateAccountNumber(formData.account_number, currentValue));
                                  }
                                  setOpenBankCombobox(false);
                                }}
                              >
                                <Check className={cn('mr-2 h-4 w-4', formData.bank_name === bank.label ? 'opacity-100' : 'opacity-0')} />
                                {bank.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="debit-form-group">
                  <label>Branch Name</label>
                  <input type="text" name="branch_name" value={formData.branch_name} readOnly />
                </div>
              </div>

              <div className="debit-form-row">
                <div className="debit-form-group">
                  <label>Universal Bank Branch Code</label>
                  <input type="text" name="branch_code" value={formData.branch_code} readOnly />
                </div>

                <div className="debit-form-group">
                  <label>Account Number <span className="required-asterisk">*</span></label>
                  <input
                    type="text"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleInputChange}
                    placeholder="Enter account number"
                    className={accountNumberWarning ? 'warning-input' : ''}
                    required
                  />
                  {accountNumberWarning && <p className="warning-message">{accountNumberWarning}</p>}
                </div>
              </div>

              <div className="debit-form-group">
                <label>Type of Account <span className="required-asterisk">*</span></label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input type="radio" name="account_type" value="Current" checked={formData.account_type === 'Current'} onChange={handleInputChange} />
                    <span>Current (aka Cheque)</span>
                  </label>
                  <label className="radio-label">
                    <input type="radio" name="account_type" value="Savings" checked={formData.account_type === 'Savings'} onChange={handleInputChange} />
                    <span>Savings</span>
                  </label>
                  <label className="radio-label">
                    <input type="radio" name="account_type" value="Transmission" checked={formData.account_type === 'Transmission'} onChange={handleInputChange} />
                    <span>Transmission</span>
                  </label>
                </div>
              </div>

              <div className="debit-form-row">
                <div className="debit-form-group">
                  <label>Date of Signing (Start Date) <span className="required-asterisk">*</span></label>
                  <input type="date" name="signing_date" value={formData.signing_date} onChange={handleInputChange} required />
                </div>

                <div className="debit-form-group">
                  <label>Contact Number (cellphone preferred) <span className="required-asterisk">*</span></label>
                  <input
                    type="tel"
                    name="contact_number"
                    value={formData.contact_number}
                    readOnly
                    className="auto-populated-field"
                  />
                </div>
              </div>

              <div className="debit-form-group">
                <label>Email Address <span className="required-asterisk">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  readOnly
                  className="auto-populated-field"
                />
              </div>

              <div className="debit-form-group">
                <label>Amount</label>
                <input type="text" name="amount" value={formData.amount} readOnly />
              </div>
            </div>

            <div className="debit-form-section beneficiary-section">
              <h3 className="subsection-title">Beneficiary Information</h3>
              <div className="beneficiary-info">
                <p><strong>To (Name of Beneficiary):</strong> TC Auditors (Pty) Ltd</p>
                <p><strong>Beneficiary Address:</strong> 42 Boet Kruger Street, Meyerton, 1961</p>
                <p><strong>Abbreviated Shortname:</strong> TCAUDITOR</p>
              </div>

              <div className="debit-form-group">
                <label className="contract-reference-label">
                  Contract Reference Number (your account number with us) <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  name="contract_reference"
                  value={formData.contract_reference}
                  onChange={handleInputChange}
                  placeholder="Enter contract reference number"
                  className={contractReferenceError ? 'input-error' : (formData.contract_reference ? 'contract-reference-value' : '')}
                  required
                  maxLength={6}
                />
                <p className="contract-reference-helper">
                  <strong>New clients:</strong> Leave unchanged. <strong>Existing clients:</strong> Update to your current account number.
                </p>
                {contractReferenceError && <span className="error-message">{contractReferenceError}</span>}
              </div>
            </div>

            <div className="debit-form-section">
              <div className="authorization-text">
                <p>
                  I hereby authorise Netcash (Pty) Ltd to issue and deliver payment instructions to your banker 
                  for collection against my abovementioned account at my abovementioned bank on condition that 
                  the sum of such payment instructions will not differ from my obligations as agreed to in the 
                  Contract Reference Number.
                </p>
                <p>
                  The individual payment instructions so authorised must be issued and delivered on the date when 
                  the obligation in terms of the Agreement is due and the amount of each individual payment 
                  instruction may not differ as agreed to in terms of the Agreement.
                </p>
                <p>
                  The payment instructions so authorised to be issued must carry the Contract Reference Number, 
                  included in the said payment instructions, and must be provided to identify the specific contract. 
                  The said Contract Reference Number should be added to this form in section E before the issuing 
                  of any payment instruction and communicated directly after having been completed.
                </p>
                <p>
                  I agree that the first payment instruction will be issued and delivered on invoice date and 
                  thereafter regularly on any subsequent invoice dates.
                </p>
                <p>
                  If however, the date of the payment instruction falls on a non-processing day (weekend or public 
                  holiday) I agree that the payment instruction may be debited against my account on the following 
                  business day; or
                </p>
                <p>
                  Subsequent payment instructions will continue to be delivered in terms of this authority until 
                  the obligations in terms of the Agreement have been paid or until this authority is cancelled by 
                  me by giving you notice in writing of not less than 7 working days and sent by prepaid registered 
                  post, delivered to your address indicated above, or per email to <strong><u>admin@tcauditors.co.za</u></strong>. I agree 
                  all dishonoured/unpaid/bounced debit orders carry a R50 admin fee.
                </p>
              </div>
            </div>

            <div className="debit-form-section">
              <h2 className="debit-section-title">B. MANDATE</h2>
              <p className="mandate-text">
                I acknowledge that all payment instructions issued by you will be treated by my abovementioned 
                bank as if the instructions had been issued by me personally.
              </p>
            </div>

            <div className="debit-form-section">
              <h2 className="debit-section-title">C. CANCELLATION</h2>
              <p className="mandate-text">
                I agree that although this authority and mandate may be cancelled by me, such cancellation 
                will not cancel the Agreement. I also understand that I cannot reclaim amounts, which have 
                been withdrawn from my account (paid) in terms of this authority and mandate if such amounts 
                were legally owing by me.
              </p>
            </div>

            <div className="debit-form-section">
              <h2 className="debit-section-title">D. ASSIGNMENT</h2>
              <p className="mandate-text">
                I acknowledge that this authority may be ceded or assigned to a third party if the Agreement 
                is also ceded or assigned to that third party.
              </p>
            </div>

            <div className="debit-form-section">
              <h2 className="debit-section-title">E. AGREEMENT REFERENCE NUMBER</h2>
              <label className="agreement-reference-label">The agreement reference number is</label>
              <input
                type="text"
                name="agreement_reference"
                value={formData.contract_reference}
                className="agreement-reference-field"
                readOnly
              />
            </div>

            <div className="debit-form-section">
              <h2 className="debit-section-title">F. RECORD OF CLIENT ACKNOWLEDGEMENTS</h2>
              <div className="acknowledgements-container">
                <div className="acknowledgement-item">
                  <input type="checkbox" id="agreement1" checked={acknowledgements.agreement1} onChange={() => handleAcknowledgementChange('agreement1')} className="acknowledgement-checkbox" />
                  <label htmlFor="agreement1" className="acknowledgement-label">I accept this debit order mandate and authorise Netcash (Pty) Ltd to issue and deliver payment instructions to my bank.</label>
                </div>
                <div className="acknowledgement-item">
                  <input type="checkbox" id="agreement2" checked={acknowledgements.agreement2} onChange={() => handleAcknowledgementChange('agreement2')} className="acknowledgement-checkbox" />
                  <label htmlFor="agreement2" className="acknowledgement-label">I understand that cancelling or revoking this mandate does not legally relieve me of any liability towards TC Auditors (Pty) Ltd, nor does it cancel any existing agreement with TC Auditors (Pty) Ltd.</label>
                </div>
                <div className="acknowledgement-item">
                  <input type="checkbox" id="agreement3" checked={acknowledgements.agreement3} onChange={() => handleAcknowledgementChange('agreement3')} className="acknowledgement-checkbox" />
                  <label htmlFor="agreement3" className="acknowledgement-label">I understand that cancelling or revoking this mandate results in an administrative surcharge of R150.00 on every invoice.</label>
                </div>
                <div className="acknowledgement-item">
                  <input type="checkbox" id="agreement4" checked={acknowledgements.agreement4} onChange={() => handleAcknowledgementChange('agreement4')} className="acknowledgement-checkbox" />
                  <label htmlFor="agreement4" className="acknowledgement-label">I understand that this is a variable mandate, meaning the amount may vary due to additional services requested by me or due to price increases.</label>
                </div>
                <div className="acknowledgement-item">
                  <input type="checkbox" id="agreement5" checked={acknowledgements.agreement5} onChange={() => handleAcknowledgementChange('agreement5')} className="acknowledgement-checkbox" />
                  <label htmlFor="agreement5" className="acknowledgement-label">I understand that an administration fee of R50.00 applies to every dishonoured or returned debit order.</label>
                </div>
              </div>
            </div>

            <div className="debit-form-section signature-section">
              <h3 className="subsection-title">Account Holder Signature</h3>
              <p className="signature-instruction">Please draw your signature in the box below:</p>
              
              <div className="signature-container">
                <SignatureCanvas
                  ref={signatureRef}
                  penColor="#000000"
                  canvasProps={{ className: 'signature-canvas' }}
                  onEnd={() => {
                    // Save signature data for restoration after resize
                    if (signatureRef.current && !signatureRef.current.isEmpty()) {
                      signatureDataRef.current = signatureRef.current.toDataURL();
                    }
                  }}
                />
              </div>
              
              <p className="signature-account-text">SIGNATURE AS USED FOR OPERATING ON THE ACCOUNT</p>
              
              <button type="button" onClick={clearSignature} className="clear-signature-btn no-print">
                Clear Signature
              </button>
            </div>

            <div className="form-actions no-print">
              <a 
                href="https://tax.tcauditors.co.za"
                className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm mr-3 no-print"
                data-hide-in-pdf="true"
              >
                Start Over
              </a>
              <Button variant="outline" onClick={handleBack} className="mr-4">
                ← Back to Page 6
              </Button>
              
              <Button 
                onClick={handleFinalize} 
                disabled={isLoading}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Processing...' : 'Finalise, Print, and Send...'}
              </Button>
            </div>

            <div className="letterhead-footer-container">
              <img src="/debit-order/letterhead-footer.png" alt="TC Auditors Footer" className="letterhead-footer" />
            </div>
          </div>
        </div>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={7} totalPages={7} pageTitle="Debit Order Mandate" />
        <img src={TCA_SHORT_LOGO} alt="TCA Logo" className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80" />
      </div>

      {/* Saving Modal */}
      {showSavingModal && (
        <div className="modal-overlay">
          <div className="modal-content saving-modal-content">
            <div className="spinner"></div>
            <h2>Generating Your PDF...</h2>
            <p className="modal-description">Please wait while we generate your signed PDF document.</p>
            <div className="countdown-timer" style={{ 
              fontSize: '32px', 
              fontWeight: 'bold', 
              color: '#1e3a8a', 
              margin: '20px 0',
              fontFamily: 'monospace'
            }}>
              {Math.floor(savingCountdown / 60)}:{String(savingCountdown % 60).padStart(2, '0')}
            </div>
            {timerResetCount === 0 ? (
              <p style={{ 
                fontSize: '14px', 
                color: '#666', 
                fontStyle: 'italic',
                maxWidth: '350px',
                textAlign: 'center',
                lineHeight: '1.5'
              }}>
                😊 Don't worry, it won't take that long! This timer is just to show you that something is happening in the background. It definitely won't take the full 2 minutes!
              </p>
            ) : (
              <div style={{ 
                fontSize: '14px', 
                color: '#666', 
                fontStyle: 'italic',
                maxWidth: '400px',
                textAlign: 'center',
                lineHeight: '1.6'
              }}>
                <p style={{ marginBottom: '16px' }}>
                  ⚡ Oops, it is taking longer than expected! Our system has upgraded the CPU processing power of your application to jump the queue. Hopefully now with the upgraded power, the server will finalise your application within the next few seconds.
                </p>
                <p style={{ 
                  color: '#888',
                  fontSize: '13px',
                  borderTop: '1px solid #eee',
                  paddingTop: '12px',
                  marginTop: '8px'
                }}>
                  PS: If this timer reaches 0:00 again then it often means the internet connectivity is bad. Please try again later when your signal is better or when you have access to a laptop with good internet speeds.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Validation Error Modal */}
      {showValidationModal && (
        <div className="modal-overlay">
          <div className="modal-content validation-modal">
            <div className="validation-modal-header">
              <span className="validation-icon">⚠️</span>
              <h2>Please Complete Required Fields</h2>
            </div>
            
            {validationErrors.fields.length > 0 && (
              <div className="validation-section">
                <h3>Missing Required Fields:</h3>
                <ul className="validation-list">
                  {validationErrors.fields.map((field, index) => (
                    <li key={index} className="validation-item missing-field">
                      <span className="validation-bullet">❌</span>
                      {field}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationErrors.errors.length > 0 && (
              <div className="validation-section">
                <h3>Validation Errors:</h3>
                <ul className="validation-list">
                  {validationErrors.errors.map((error, index) => (
                    <li key={index} className="validation-item error-item">
                      <span className="validation-bullet">⚠️</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationErrors.acknowledgements.length > 0 && (
              <div className="validation-section">
                <h3>Acknowledgements Not Accepted:</h3>
                <ul className="validation-list">
                  {validationErrors.acknowledgements.map((ack, index) => (
                    <li key={index} className="validation-item unchecked-ack">
                      <span className="validation-bullet">☐</span>
                      {ack}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                onClick={() => setShowValidationModal(false)} 
                className="send-btn validation-close-btn"
              >
                OK, I&apos;ll Complete These
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Dialog */}
      {showSuccessDialog && (
        <div className="modal-overlay">
          <div className="modal-content success-modal">
            <h2>Tax Services Agreement Successfully Signed!</h2>
            {emailSendFailed ? (
              <div
                data-testid="email-warning-banner"
                style={{
                  background: '#fef2f2',
                  border: '2px solid #dc2626',
                  borderRadius: '8px',
                  padding: '16px',
                  margin: '16px 0',
                  textAlign: 'left',
                }}
              >
                <p style={{ color: '#991b1b', fontWeight: 700, fontSize: '1.05em', margin: '0 0 8px 0' }}>
                  ⚠ Email Delivery Issue
                </p>
                <p style={{ color: '#7f1d1d', margin: 0, lineHeight: 1.6 }}>
                  Your Tax Services Agreement has been signed and saved successfully, but we were unable to
                  confirm delivery of your confirmation email due to an unexpected error on our end.
                  <br /><br />
                  <strong>Please report this to TC Auditors</strong> so they can investigate and resend your
                  documents — call the Help Desk on <strong>060 015 1110</strong> or email{' '}
                  <a href="mailto:admin@tcauditors.co.za" style={{ color: '#991b1b', textDecoration: 'underline' }}>
                    admin@tcauditors.co.za
                  </a>.
                </p>
              </div>
            ) : (
              <p className="modal-description success-message">
                Thank you for signing the Tax Services Agreement, a signed PDF copy has been sent to you and to the TCAuditors' Tax Team for processing.
                <br />
                <small>You may close this tab/screen on your browser now.</small>
              </p>
            )}
            <div className="modal-actions">
              <button onClick={handleThankYou} className="send-btn thank-you-btn">
                Thank You!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageSeven;
